import { USER_STATUS } from "@shared/constants";
import { User } from "@shared/models";
import { Option } from "@shared/interfaces";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store/";
import { CreateRecognitionDto, RecipientOption, RecognitionDrawerValues } from "@containers/Recognition/interfaces";

export const RECOGNITION_SCORE_OPTIONS: Option<number>[] = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 15, label: "15" },
  { value: 20, label: "20" },
];

/**
 * 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: `initialUser` prefills the "To" recipient row for the Leaderboard
 * "Recognise" nudge and the Spread your Sparks cards -- OPTIONAL, and every existing call site that omits
 * it must keep getting a blank picker. Core value and score never come from a prop: every existing caller
 * of this drawer opens with neither chosen, `score` defaulting to the first (lowest) option, matching the
 * old dropdown form's own default.
 *
 * @param initialUser - the Option to prefill the "To" row with, or `undefined` for a blank picker.
 */
export const getInitValues = (initialUser?: Option): RecognitionDrawerValues => ({
  description: "",
  score: RECOGNITION_SCORE_OPTIONS[0].value,
  user: initialUser ?? null,
  core_value: null,
});

export const prepareSubmitValue = (values: RecognitionDrawerValues): CreateRecognitionDto => {
  return {
    title: values.core_value?.title || "",
    description: values.description,
    score: values.score,
    user_id: Number(values.user?.value),
    core_value_id: Number(values.core_value?.id),
  };
};

/**
 * Derives a "For" row's glyph letter from the core value's own title -- the first character, uppercased.
 * A deliberate, local duplicate of `Leaderboard/crownBadgeLetter.ts`'s identical one-line rule (never a
 * hardcoded per-value acronym, for the same reason that file documents: a fixed lookup would silently
 * diverge the moment a title changes). Kept as its own copy here rather than a cross-feature import --
 * this drawer has no other reason to depend on the Leaderboard folder, and the rule is one line.
 *
 * @param title - the core value's own title.
 * @returns the title's first character, uppercased.
 */
export const coreValueGlyphLetter = (title: string): string => title.charAt(0).toUpperCase();

/**
 * Derives the "To" row's avatar initials from a recipient `Option`'s `label` -- the only shape a
 * recipient ever arrives in here, whether prefilled (`recipient` prop, always `{ label: fullName, value:
 * id }`) or chosen from the search picker (`handlers.user.prepareOptionFunction` below produces the same
 * shape). Unlike `InitialsAvatar`'s `getInitials`, which takes separate first/last names, there is no
 * richer user record available at this call site to split apart. First word + last word (not first two
 * words), so a middle name doesn't win out over the surname.
 *
 * @param label - the recipient Option's full-name label.
 * @returns up to two initials, uppercased; empty string for a blank label.
 */
export const recipientInitials = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

/**
 * Derives the "To" row's first name for the footer caption ("<FirstName> sees it once approved") from
 * the same recipient `Option.label` `recipientInitials` above reads. The first whitespace-separated word,
 * matching how every `prepareOptionFunction` in this app builds `label` as `${first_name} ${last_name}`.
 *
 * @param label - the recipient Option's full-name label.
 * @returns the label's first word, or "" for a blank label.
 */
export const recipientFirstName = (label: string): string => label.trim().split(/\s+/)[0] ?? "";

export const handlers = {
  user: {
    selectData: sharedSelectors.getUsers,
    loadingAction: sharedActions.getUsers,
    // google-profile-pictures-plan.md Phase 2 step 4: carries the picture through selection, so the
    // chosen-recipient row (RecognitionForm.tsx) can render the same photo the search result showed
    // rather than losing it the moment a person is picked.
    prepareOptionFunction: (u: User): RecipientOption => {
      return { label: `${u.first_name} ${u.last_name}`, value: String(u.id), avatarUrl: u.avatar_url };
    },
    getData: (search?: string) => {
      return sharedActions.getUsers.request({
        page: 0,
        limit: 500,
        search: search || "",
        status: USER_STATUS.ACTIVE,
      });
    },
    onSort: () => {
      return (a: Option, b: Option) => {
        return a.label.localeCompare(b.label);
      };
    },
  },
};
